import { SandboxQueryState } from "@/components/app/sandbox-catalog";
import { ChangeTracker } from "@/components/app/changelog/item";
import { DynamicPagination } from "@/components/app/dynamic-pagination";
import { formatSandboxCount, SandboxPageHeader } from "@/components/app/sandbox-layout";
import { httpClient } from "@/lib/http-client";
import { HydrationBoundary, keepPreviousData, useQuery } from "@tanstack/react-query";
import { FileClock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/lib/paraglide-react";
interface ChangelogResponse {
  hits: (OfferHit | ItemHit | AssetHit | Hit)[];
  query: string;
  processingTimeMs: number;
  limit: number;
  offset: number;
  estimatedTotalHits: number;
}
interface DefaultHit {
  _id: string;
  timestamp: string;
  metadata: Metadata;
}
interface Metadata {
  changes: Change[];
  contextId: string;
  contextType: string;
}
interface Change {
  changeType: "insert" | "update" | "delete";
  field: string;
  newValue: unknown;
  oldValue: unknown;
}
interface OfferHit extends DefaultHit {
  metadata: Metadata & {
    contextType: "offer";
  };
  document: SingleOffer;
}
interface ItemHit extends DefaultHit {
  metadata: Metadata & {
    contextType: "item";
  };
  document: SingleItem;
}
interface AssetHit extends DefaultHit {
  metadata: Metadata & {
    contextType: "asset";
  };
  document: SingleItem;
}
interface Hit {
  _id: string;
  timestamp: string;
  metadata: Metadata;
  document: null;
}
import { Route } from "/app/src/routes/{-$locale}/sandboxes/$id/changelog.tsx";
function RouteComponent() {
  const {
    t
  } = useTranslation();
  const {
    id
  } = Route.useParams();
  const [page, setPage] = useState(1);
  const {
    data,
    isPending: isLoading,
    isError,
    isFetching,
    refetch
  } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ["changelog", {
      id,
      page,
      limit: 20
    }],
    queryFn: () => httpClient.get<ChangelogResponse>(`/sandboxes/${id}/changelog`, {
      params: {
        page,
        limit: 20
      }
    })
  });
  if (isLoading) {
    return <div className="flex flex-col gap-6 w-full">
        <SandboxPageHeader icon={FileClock} eyebrow={t("sandboxes.changelogEyebrow")} title={t("sandboxes.changelogTitle")} description={t("sandboxes.changelogDescription")} stats={[{
        label: t("sandboxes.totalChangesLabel"),
        value: t("sandboxCatalog.loading")
      }]} />
        <div className="grid grid-cols-1 gap-4 w-full">
          {Array.from({
          length: 5
        }).map((_, index) => <div key={index} className="h-28 animate-pulse rounded-md bg-muted" />)}
        </div>
      </div>;
  }
  if (isError) return <SandboxQueryState error retry={() => void refetch()} />;
  return <div className="flex flex-col gap-6 w-full">
      <SandboxPageHeader icon={FileClock} eyebrow={t("sandboxes.changelogEyebrow")} title={t("sandboxes.changelogTitle")} description={t("sandboxes.changelogDescription")} stats={[{
      label: t("sandboxes.totalChangesLabel"),
      value: formatSandboxCount(data?.estimatedTotalHits)
    }]} />
      {isFetching && <p role="status" className="text-sm text-muted-foreground">
          {t("sandboxCatalog.refreshing")}
        </p>}
      <div className="grid grid-cols-1 gap-4 w-full">
        {data?.hits
      // Filter out hits without metadata
      .filter(hit => hit.metadata).map(hit => <ChangeTracker key={hit._id} {...{
        _id: hit._id,
        document: hit.document,
        metadata: hit.metadata,
        timestamp: hit.timestamp
      } as Parameters<typeof ChangeTracker>[0]} />)}
      </div>
      {data?.hits?.length === 0 && <SandboxQueryState empty />}
      {(data?.hits?.length ?? 0) > 0 && <DynamicPagination totalPages={data ? Math.ceil(data.estimatedTotalHits / data.limit) : 0} currentPage={page} setPage={setPage} />}
    </div>;
}
const SplitComponent = () => {
  const {
    dehydratedState
  } = Route.useLoaderData() as {
    dehydratedState: DehydratedState;
    params: {
      id: string;
    };
  };
  return <HydrationBoundary state={dehydratedState}>
        <RouteComponent />
      </HydrationBoundary>;
};
export { SplitComponent as component };